/**
 * text/event-stream のフレーム分解だけを担当する。
 * fetchのstream readerはUTF-8のchunk境界や複数data行をまたぐため、
 * 「行に分ける」処理と「イベントの意味づけ」を分けてテストできるようにしている。
 * api-contract.md 6章。
 */

export interface SseFrame {
  /** SSEの `event:` 行。未指定は仕様どおり 'message'。 */
  event: string;
  /** 複数 `data:` 行を改行で連結した本文。 */
  data: string;
  /** `id:` 行。永続SSEのcursorとして使う。 */
  lastEventId: string | null;
  /**
   * `retry:` 行のミリ秒。
   * この基盤の再接続間隔は契約で1〜30秒と決まっているため使わないが、
   * SSEのフレームとしては解析しておく（未知fieldとして落とすと行の意味が消えるため）。
   */
  retryMs: number | null;
}

const FIELD_SEPARATOR = ':';

/**
 * chunk境界をまたいで行を組み立てるパーサ。
 * decodeはTextDecoder({stream:true})側の責務とし、ここは文字列だけを扱う。
 */
export class SseParser {
  private buffer = '';
  private dataLines: string[] = [];
  private eventName: string | null = null;
  private lastEventId: string | null = null;
  private retryMs: number | null = null;

  /** 受け取った文字列断片から、完成したフレームだけを返す。 */
  push(chunk: string): SseFrame[] {
    this.buffer += chunk;
    const frames: SseFrame[] = [];

    // \r\n / \n / \r のいずれも行区切りとして扱う（SSE仕様）。
    let index: number;
    while ((index = this.indexOfLineBreak(this.buffer)) >= 0) {
      const line = this.buffer.slice(0, index);
      this.buffer = this.buffer.slice(index + this.lineBreakLength(this.buffer, index));
      const frame = this.consumeLine(line);
      if (frame) frames.push(frame);
    }
    return frames;
  }

  /** 接続終了時に、末尾の未確定行を捨てたことを明示するために呼ぶ。 */
  reset(): void {
    this.buffer = '';
    this.dataLines = [];
    this.eventName = null;
    this.retryMs = null;
    // lastEventId は再接続時のLast-Event-IDに使うため保持する。
  }

  get currentLastEventId(): string | null {
    return this.lastEventId;
  }

  private indexOfLineBreak(text: string): number {
    for (let i = 0; i < text.length; i += 1) {
      const c = text[i];
      if (c === '\n') return i;
      if (c === '\r') {
        // \r が末尾の場合、次のchunkが \n で始まる可能性があるので確定させない。
        if (i === text.length - 1) return -1;
        return i;
      }
    }
    return -1;
  }

  private lineBreakLength(text: string, index: number): number {
    return text[index] === '\r' && text[index + 1] === '\n' ? 2 : 1;
  }

  private consumeLine(line: string): SseFrame | null {
    if (line === '') return this.flush();
    if (line.startsWith(FIELD_SEPARATOR)) return null; // heartbeat等のコメント行

    const separator = line.indexOf(FIELD_SEPARATOR);
    const field = separator < 0 ? line : line.slice(0, separator);
    let value = separator < 0 ? '' : line.slice(separator + 1);
    if (value.startsWith(' ')) value = value.slice(1);

    switch (field) {
      case 'event':
        this.eventName = value;
        break;
      case 'data':
        this.dataLines.push(value);
        break;
      case 'id':
        // SSE仕様が無視を求めるのはU+0000を含むidだけ。
        if (!value.includes('\u0000')) this.lastEventId = value;
        break;
      case 'retry': {
        const parsed = Number(value);
        if (Number.isInteger(parsed) && parsed >= 0) this.retryMs = parsed;
        break;
      }
      default:
        break; // 未知fieldは無視（SSE仕様）
    }
    return null;
  }

  private flush(): SseFrame | null {
    if (this.dataLines.length === 0 && this.eventName === null && this.retryMs === null) {
      return null;
    }
    const frame: SseFrame = {
      event: this.eventName ?? 'message',
      data: this.dataLines.join('\n'),
      lastEventId: this.lastEventId,
      retryMs: this.retryMs,
    };
    this.dataLines = [];
    this.eventName = null;
    this.retryMs = null;
    return frame;
  }
}

/** 再接続待ち時間。api-contract.md の 1〜30秒 backoff。 */
export function reconnectDelayMs(attempt: number, jitter = Math.random()): number {
  const base = Math.min(30_000, 1_000 * 2 ** Math.max(0, attempt));
  // 同時再接続の集中を避けるため±25%ゆらした上で、契約の1〜30秒へ収める。
  const spread = base * 0.25;
  const jittered = Math.round(base - spread + jitter * spread * 2);
  return Math.min(30_000, Math.max(1_000, jittered));
}
